import { useState, useEffect, RefObject } from 'react';
import {
    measureContainer,
    clampScale,
    createScheduler,
    isBrowser,
    isResizeObserverSupported,
} from '@eduvidu/react-autoscale';

/**
 * Hook to calculate dynamic scale factor for worklist tables.
 * Powered by @eduvidu/autoscale-engine core utilities — production-ready,
 * StrictMode-safe scaling with ResizeObserver + rAF batching.
 *
 * Uses the library's `measureContainer`, `clampScale`, and `createScheduler`
 * while preserving the existing single-ref API pattern used throughout the
 * symbian-rad codebase.
 *
 * @param containerRef - Reference to the container element
 * @param minScale - Minimum scale factor to maintain readability (default: 0.6)
 * @returns Current scale factor (minScale – 1.0)
 */
const useWorklistScale = (
    containerRef: RefObject<HTMLDivElement>,
    minScale: number = 0.6
): number => {
    const [scale, setScale] = useState(1);

    useEffect(() => {
        if (!isBrowser() || !isResizeObserverSupported()) return;
        if (!containerRef.current) return;

        // Fixed minimum table width based on grid column definitions
        // Sum of: 50px + 160px + 80px + 100px + 100px + 100px + 120px + 90px + 70px + 50px + 70px = 990px
        // Plus gaps (10 gaps × 12px = 120px) = 1110px
        // Plus padding (2 × 24px = 48px) = 1158px
        // Rounded to 1166px for safety margin
        const TABLE_MIN_WIDTH = 1166;

        const scheduler = createScheduler();

        const recalculate = () => {
            const container = containerRef.current;
            if (!container) return;

            const { width: containerWidth } = measureContainer(container);

            // Safety check: skip if container too small
            if (containerWidth < 100) return;

            if (TABLE_MIN_WIDTH > containerWidth) {
                const raw = containerWidth / TABLE_MIN_WIDTH;
                setScale(clampScale(raw, minScale, 1));
            } else {
                setScale(1);
            }
        };

        // Initial measurement
        scheduler.schedule(recalculate);

        // Observe container size changes
        const observer = new ResizeObserver(() => {
            scheduler.schedule(recalculate);
        });
        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
            scheduler.destroy();
        };
    }, [containerRef, minScale]);

    return scale;
};

export default useWorklistScale;
